import * as React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import * as classnames from 'classnames';
import { ProductNotifyMeConfirmation } from './ProductNotifyMeConfirmation';
import { ProductNotifyMeForm } from './ProductNotifyMeForm';
import { ProductNotifyMeNotice } from './ProductNotifyMeNotice';
import { Button } from '@estee/elc-buttons';
import { NotifyMeForm } from '../../domain/entities/NotifyMeForm';
import { Body2 } from '@estee/elc-typography';
import { translate, config, ServiceView } from '@estee/elc-service';
import { EMAIL_OPT_IN } from '@estee/elc-service-view-names';
import { ProductNotifyMeInfo } from './ProductNotifyMeInfo';

const { name } = __serviceInfo__;

export enum Translations {
    notifyMeTitleLabel,
    notifyMeDescriptionLabel,
    emailLabel,
    emailAddressRequiredMessage,
    validEmailLabel,
    notifyMeSubmitButtonLabel,
    gdprValidationText
}

export type ITranslations = { [key in keyof typeof Translations]: string };

export interface ICompliance {
    gdpr?: boolean;
}

export interface IWaitlist {
    enabled?: boolean;
    enabledByStatus?: string[];
    showEmailOptin?: boolean;
    enableHybridModal?: boolean;
}

interface IConfigProps {
    compliance?: ICompliance;
    waitlist?: IWaitlist;
    emailOptin?: { enabled?: boolean };
}

export interface IProductNotifyMePopUpContent {
    config?: IConfigProps;
    content: string;
    productDisplayName: string;
    notifyMeForm: NotifyMeForm;
    confirmationContent: string;
    confirmationContentTitle: string;
    zIndex?: number;
    isProductNotifyMeConfirmationOpen: boolean;
    translations?: ITranslations;
    acceptsTerms: boolean;
    onTermsCheckedChange(value: boolean): void;
    onSubmitButtonClick(gdpr: boolean): void;
    onCloseButtonClick(): void;
    onNotifyMeNoticeInfoClick(): void;
    isGdprPopupOpen?: boolean;
    gdprConfirmation?: boolean;
}

const ProductNotifyMeContent = styled.div`
    margin: 0 auto;
    padding: 50px;
`;

const SubmitButton = styled(Button)`
    width: 100%;
    height: 35px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
`;

const EmailOptIn = styled.div`
    white-space: nowrap;
`;

const productNotifyMeContentClassNames = classnames(
    'elc-product-notify-me-content',
    'js-product-notify-me-content'
);

const productNotifyMeTitleLabelClassNames = classnames(
    'elc-product-notify-me-title-label',
    'js-product-notify-me-title-label'
);

const productNotifyMeDescriptionLabelClassNames = classnames(
    'elc-product-notify-me-description-label',
    'js-product-notify-me-description-label'
);

@config(['compliance', 'waitlist', 'emailOptin'])
@translate([
    'notifyMeTitleLabel',
    'notifyMeDescriptionLabel',
    'emailLabel',
    'emailAddressRequiredMessage',
    'validEmailLabel',
    'notifyMeSubmitButtonLabel',
    'gdprValidationText'
])
@observer
export class ProductNotifyMePopUpContent extends React.Component<IProductNotifyMePopUpContent> {
    public onSubmitButtonClick = () => {
        const { compliance } = this.props.config as IConfigProps;
        const gdpr = Boolean(compliance && compliance.gdpr);

        this.props.onSubmitButtonClick(gdpr);
    };

    public render() {
        const isEmailValid = this.props.notifyMeForm.isValid;

        const productNotifyMeSubmitButtonClassNames = classnames({
            'elc-product-notify-me-submit-button': true,
            'js-product-notify-me-submit-button': true,
            'elc-product-notify-me-submit-button-disabled': !isEmailValid,
            'js-product-notify-me-submit-button-disabled': !isEmailValid
        });

        const {
            emailAddressRequiredMessage,
            emailLabel,
            notifyMeSubmitButtonLabel,
            validEmailLabel,
            notifyMeTitleLabel,
            notifyMeDescriptionLabel
        } = this.props.translations as ITranslations;

        const { compliance, waitlist, emailOptin } = this.props.config as IConfigProps;
        const emailInputPlaceholder = emailLabel;
        const isConfirmationModalOpen = this.props.isProductNotifyMeConfirmationOpen;
        const gdpr = (compliance && compliance.gdpr) || false;

        const waitlistEmailOptin = (waitlist && waitlist.showEmailOptin) || false;
        const emailOptinConfig = (emailOptin && emailOptin.enabled) || false;
        const enableEmailOptin = emailOptinConfig && waitlistEmailOptin;

        const { gdprConfirmation = false } = this.props;

        return (
            <ProductNotifyMeContent className={productNotifyMeContentClassNames}>
                <Body2 className={productNotifyMeTitleLabelClassNames}>{notifyMeTitleLabel}</Body2>
                <Body2 className={productNotifyMeDescriptionLabelClassNames}>
                    {notifyMeDescriptionLabel}
                </Body2>
                <ProductNotifyMeForm
                    notifyMeForm={this.props.notifyMeForm}
                    emailInputPlaceholder={emailInputPlaceholder}
                    emailAddressRequired={emailAddressRequiredMessage}
                    isEmailValidMessage={validEmailLabel}
                />
                {gdpr && (
                    <>
                        <ProductNotifyMeNotice
                            onTermsCheckedChanged={this.props.onTermsCheckedChange}
                            acceptsTerms={this.props.acceptsTerms}
                            gdprConfirmation={gdprConfirmation}
                        />
                        <ProductNotifyMeInfo
                            onNotifyMeNoticeInfoClick={this.props.onNotifyMeNoticeInfoClick}
                            isGdprPopupOpen={this.props.isGdprPopupOpen}
                        />
                    </>
                )}
                {enableEmailOptin && (
                    <EmailOptIn>
                        <ServiceView name={EMAIL_OPT_IN} serviceName={name} />
                    </EmailOptIn>
                )}
                <SubmitButton
                    className={productNotifyMeSubmitButtonClassNames}
                    onClick={this.onSubmitButtonClick}
                >
                    {notifyMeSubmitButtonLabel}
                </SubmitButton>
                <ProductNotifyMeConfirmation
                    productDisplayName={this.props.productDisplayName}
                    onCloseButtonClick={this.props.onCloseButtonClick}
                    confirmationContent={this.props.confirmationContent}
                    confirmationContentTitle={this.props.confirmationContentTitle}
                    isPopUpOpen={isConfirmationModalOpen}
                    zIndex={this.props.zIndex}
                />
            </ProductNotifyMeContent>
        );
    }
}
